import type { ImprovementLine, ScenarioImprovementView } from '@/api/scenarioIntelligence';
import type { EvaluationScenario } from '@/api/scenarioVersions';

// ─────────────────────────────────────────────────────────────────────────────
// Screen 04, the AI improvement: how a proposal's lines sit on the three columns (Original / Yours / Current), the
// sentence under each field, and the draft kept in this browser. Pure, so the drawer only draws. The server decides every
// line, control and approval effect; nothing here judges a version.
// ─────────────────────────────────────────────────────────────────────────────

export interface ImprovementField {
  key: string;
  label: string;
  /** The proposal's lines for this field, in the server's order. */
  lines: ImprovementLine[];
}

/** One field per part a proposal can change, each with its lines; a field no line touches is still drawn as unchanged. */
export function fieldsOf(original: EvaluationScenario, lines: ImprovementLine[]): ImprovementField[] {
  const fields: ImprovementField[] = [
    { key: 'purpose', label: 'What this scenario tests', lines: lines.filter((l) => l.kind === 'Purpose') },
  ];
  const turns = (original.events ?? []).filter((e) => e.actor === 'User').sort((a, b) => a.turnIndex - b.turnIndex);
  for (const e of turns) {
    fields.push({
      key: `turn:${e.turnIndex}`,
      label: turns.length === 1 ? 'What the user says' : `Turn ${e.turnIndex + 1} · what the user says`,
      lines: lines.filter((l) => l.kind === 'UserWords' && l.turnIndex === e.turnIndex),
    });
  }
  fields.push({
    key: 'checks',
    label: 'Checks',
    lines: lines.filter((l) => l.kind === 'AddCheck' || l.kind === 'RemoveCheck' || l.kind === 'ReplaceCheck'),
  });
  return fields;
}

/**
 * The sentence under a field: which column the saved version takes it from. `stale` is true once someone saved a newer
 * version and changed this field too; then the field waits for a choice in the merge.
 */
export function fieldOutcome(field: ImprovementField, kept: ReadonlySet<string>, theyChanged = false): { text: string; tone: 'green' | 'amber' } {
  const mine = field.lines.filter((l) => kept.has(l.id)).length;
  const proposed = field.lines.length;
  const lines = proposed > 1 ? ` ${mine} of ${proposed} proposed lines kept.` : '';
  if (mine === 0) {
    return theyChanged
      ? { text: 'You did not change this field: Current is used, with their change.', tone: 'green' }
      : { text: `You did not change this field: Current is used.${proposed > 0 ? ' The proposed line is not kept.' : ''}`, tone: 'green' };
  }
  if (theyChanged) return { text: `Both of you changed this field: it waits for your choice in the merge.${lines}`, tone: 'amber' };
  return { text: `Only you changed this field: Yours is used.${lines}`, tone: 'green' };
}

// ── the draft, kept in this browser until it is saved or discarded ─────────────

export interface ImprovementDraft {
  view: ScenarioImprovementView;
  kept: string[];
  keptAtUtc: string;
}

const KEY = (scenarioId: string) => `agent-studio.scenario-improvement.${scenarioId}`;

/** A per-viewer convenience: the browser may refuse storage (private window, cleared data), and the drawer works without it. */
export function loadDraft(scenarioId: string): ImprovementDraft | null {
  try {
    const raw = window.localStorage.getItem(KEY(scenarioId));
    if (!raw) return null;
    const d = JSON.parse(raw) as ImprovementDraft;
    return d?.view?.proposalJson && d.view.scenarioId === scenarioId && Array.isArray(d.kept) ? d : null;
  } catch {
    return null;
  }
}

export function saveDraft(scenarioId: string, draft: ImprovementDraft): void {
  try {
    window.localStorage.setItem(KEY(scenarioId), JSON.stringify(draft));
  } catch {
    /* not kept: the drawer says so only when it restores one */
  }
}

export function clearDraft(scenarioId: string): void {
  try {
    window.localStorage.removeItem(KEY(scenarioId));
  } catch {
    /* nothing to clear */
  }
}
